// Two-step activation: user clicks magic-link → lands here → clicks "Begin audit review"
// Email pre-fetch protection: the actual POST happens on explicit button click (not on load).
import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuditorActivate } from '@/api/hooks/useAuditorAccess';

export function AuditorActivatePage() {
  const { activationToken } = useParams<{ activationToken: string }>();
  // Freeze token at mount — useRef initial value is set once and never updated,
  // so re-renders caused by store updates (isAuditorAuthenticated flipping true
  // during onSuccess) cannot wipe the value before the POST completes.
  const tokenRef = useRef(activationToken);
  const navigate = useNavigate();
  const activate = useAuditorActivate();
  const [error, setError] = useState<string | null>(null);

  async function handleActivate() {
    const token = tokenRef.current;
    if (!token) return;
    setError(null);
    try {
      const result = await activate.mutateAsync(token);
      if (result.alreadyActive) {
        // Session restored — go straight to dashboard
        navigate('/auditor/dashboard', { replace: true });
      } else {
        navigate('/auditor/dashboard', { replace: true });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.errors?.[0]?.message ?? 'Activation failed';

      if (status === 403 && msg.includes('revoked')) {
        navigate('/auditor/revoked', { replace: true });
        return;
      }
      if (status === 410) {
        navigate('/auditor/expired', { replace: true });
        return;
      }
      setError(msg);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-8 shadow-lg text-center">
        <ShieldCheck className="mx-auto mb-4 h-12 w-12 text-accent-cyan" />
        <h1 className="mb-2 text-2xl font-bold text-text-primary">Auditor Access</h1>
        <p className="mb-6 text-text-secondary">
          You have been granted read-only access to review compliance evidence.
          Click the button below to begin.
        </p>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <Button
          onClick={handleActivate}
          disabled={activate.isPending || !tokenRef.current}
          className="w-full"
          size="lg"
        >
          {activate.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Activating…
            </>
          ) : (
            'Begin Audit Review'
          )}
        </Button>
        <p className="mt-4 text-xs text-text-tertiary">
          This link is single-use. Your session is read-only — you cannot modify any records.
        </p>
      </div>
    </div>
  );
}
